// Check-in list for tournament coaches and players

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useFirebase } from './FirebaseContextProvider';
import { UsersRoles } from '../utils/enums';
import CheckBox from './CheckBox';
import { tournamentSetCheckIn } from '../utils/firebase';
import FilterInput from './FilterInput';
import Select from './Select';
import PlayerStatus from './PlayerStatus';
import moment from 'moment';
import { getTeamDivision, isTooOld } from '../utils/misc';
import {
  useWaiverStatusesBatch,
  useTournamentParts,
  useOrgUsers,
} from '../utils/hooks';
import Avatar from './Avatar';
import Spinner from './Spinner';
import './CheckInList.scss';

// Module level so the array identity cannot move under the listener
const checkInRoles = [UsersRoles.PLAYER, UsersRoles.COACH];

export default function CheckInList({ tournamentId }) {
  const { teamsById, tournamentsById, isTeamsLoaded } = useFirebase();
  // The rules only let `users` be listed by organization, so the admitted
  // teams are matched here rather than in the query
  const { users: orgPeople, isLoaded: arePeopleLoaded } =
    useOrgUsers(checkInRoles);
  const [checkedInMap, setCheckedInMap] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [division, setDivision] = useState('All divisions');
  const [team, setTeam] = useState('All teams');

  const arePartsReady = useTournamentParts(tournamentId, ['teams', 'checkIn']);
  // The team documents are a wave behind the subcollection that names them;
  // without them every row renders a blank team and division
  const isReady = arePartsReady && isTeamsLoaded && arePeopleLoaded;

  const tournament = useMemo(
    () => tournamentsById.get(tournamentId),
    [tournamentId, tournamentsById],
  );

  // Sync local cache with backend check-in state from tournament.checkIn
  useEffect(() => {
    if (!tournament || !Array.isArray(tournament.checkIn)) return;

    const map = {};
    tournament.checkIn.forEach((entry) => {
      const { id, status, time } = entry;
      map[id] = { status, time };
    });

    setCheckedInMap(map);
  }, [tournament]);

  const admittedTeamIds = useMemo(() => {
    if (!tournament?.teams) return [];
    const ids = [];
    Object.entries(tournament.teams).forEach(([, teamInfo]) => {
      if (teamInfo?.admitted !== true) return;
      ids.push(teamInfo.id);
    });
    return ids;
  }, [tournament?.teams]);

  const { waiverMap } = useWaiverStatusesBatch(admittedTeamIds);

  const participants = useMemo(() => {
    if (!admittedTeamIds.length) return [];

    const admittedTeamIdSet = new Set(admittedTeamIds);
    // The tournament's own team documents, kept subscribed by the `teams` part
    // above — not the directory, which is a list of rows and not readable to
    // everyone this component's siblings render for
    const teamMap = teamsById;

    const result = [];

    orgPeople.forEach((u) => {
      if (u.role === UsersRoles.PLAYER) {
        const teamIds = u.teams;
        if (!Array.isArray(teamIds) || !teamIds.length) return;

        teamIds.forEach((teamId) => {
          if (!admittedTeamIdSet.has(teamId)) return;
          const teamObj = teamMap.get(teamId);
          result.push({
            id: u.id,
            name: u.name,
            imgThumbPath: u.imgThumbPath,
            teamId,
            teamName: teamObj?.name,
            teamAge: teamObj?.age,
            division: getTeamDivision(teamObj),
            role: u.role,
            roleLabel: 'Player',
            birthDate: u.birthDate,
            verified: u.verified,
            tooOld: isTooOld({
              birthDate: u.birthDate,
              age: teamObj?.age,
              cutOffDate: tournament.cutOffDate,
            }),
          });
        });

        return;
      }

      // Coaches: one entry per admitted team
      if (u.role === UsersRoles.COACH) {
        if (!Array.isArray(u.teams) || u.teams.length === 0) return;

        const coachTeamIds = u.teams.filter((tid) =>
          admittedTeamIdSet.has(tid),
        );
        if (!coachTeamIds.length) return;

        coachTeamIds.forEach((teamId) => {
          const teamObj = teamMap.get(teamId);
          result.push({
            id: u.id,
            name: u.name,
            imgThumbPath: u.imgThumbPath,
            teamId,
            teamName: teamObj?.name,
            division: getTeamDivision(teamObj),
            role: u.role,
            roleLabel: 'Coach',
          });
        });
      }
    });

    const roleRank = {
      [UsersRoles.COACH]: 0,
      [UsersRoles.PLAYER]: 1,
    };

    result.sort((a, b) => {
      // The team documents land a wave after the subcollection that names
      // them, so a row can be sorted before its name exists
      const teamCmp = (a.teamName ?? '').localeCompare(b.teamName ?? '', 'en', {
        sensitivity: 'base',
      });
      if (teamCmp !== 0) return teamCmp;

      const roleA = roleRank[a.role] ?? 99;
      const roleB = roleRank[b.role] ?? 99;
      if (roleA !== roleB) return roleA - roleB;

      return a.name.localeCompare(b.name, 'en', {
        sensitivity: 'base',
      });
    });

    return result;
  }, [orgPeople, teamsById, admittedTeamIds, tournament]);

  // Build division options from unique participants.division values
  const divisions = useMemo(() => {
    const uniq = [
      ...new Set(participants.map((p) => p.division).filter(Boolean)),
    ].sort((a, b) =>
      a.localeCompare(b, 'en', {
        sensitivity: 'base',
      }),
    );

    return ['All divisions', ...uniq];
  }, [participants]);

  // Keep selected division valid when available divisions change
  useEffect(() => {
    if (!divisions.includes(division)) setDivision(divisions[0]);
  }, [divisions, division]);

  // Build team options from tournament team ids mapped to team names
  const teamsOptions = useMemo(() => {
    const names = admittedTeamIds
      .map((id) => teamsById.get(id)?.name)
      .filter(Boolean);

    const uniq = [...new Set(names)].sort((a, b) =>
      a.localeCompare(b, 'en', { sensitivity: 'base' }),
    );

    return ['All teams', ...uniq];
  }, [admittedTeamIds, teamsById]);

  // Keep selected team valid when available teams change
  useEffect(() => {
    if (!teamsOptions.includes(team)) setTeam(teamsOptions[0]);
  }, [teamsOptions, team]);

  // Map team name to its division for automatic division selection
  const teamDivisionMap = useMemo(() => {
    const map = {};
    participants.forEach((p) => {
      if (!p.teamName || !p.division) return;
      if (map[p.teamName]) return;
      map[p.teamName] = p.division;
    });
    return map;
  }, [participants]);

  // Filter participants by division and team
  const filteredParticipants = useMemo(() => {
    return participants.filter((p) => {
      const byDivision =
        division === 'All divisions' ? true : p.division === division;
      if (!byDivision) return false;

      const byTeam = team === 'All teams' ? true : p.teamName === team;
      if (!byTeam) return false;

      return true;
    });
  }, [participants, division, team]);

  const searchValues = useMemo(
    () =>
      filteredParticipants.map((p) => [
        p.name,
        p.birthDate ? 'DOB: ' + p.birthDate.format('MMM DD, YY') : '',
        p.roleLabel,
        p.teamName,
        p.division,
      ]),
    [filteredParticipants],
  );

  // Filter participants by search query
  const displayedParticipants = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filteredParticipants;

    return filteredParticipants.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [filteredParticipants, searchValues, searchQuery]);

  const handleDivisionChange = useCallback((val) => {
    setDivision(val);
    setTeam('All teams');
  }, []);

  const handleTeamChange = useCallback(
    (val) => {
      setTeam(val);

      if (val === 'All teams') return;

      const div = teamDivisionMap[val];
      if (div) setDivision(div);
    },
    [teamDivisionMap],
  );

  const handleCheckChange = async (userId, teamId, value) => {
    // Optimistic UI update
    setCheckedInMap((prev) => ({
      ...prev,
      [`${teamId}-${userId}`]: { status: value, time: moment(new Date()) },
    }));

    try {
      await tournamentSetCheckIn({
        tournamentId,
        teamId,
        userId,
        checkIn: value,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CheckInList] Failed to set check-in status', error);
      setCheckedInMap((prev) => ({
        ...prev,
        [`${teamId}-${userId}`]: { status: !value },
      })); // Rollback on error
    }
  };

  // Totals per team from filteredParticipants (unaffected by search query)
  const teamTotals = useMemo(() => {
    const map = {};
    filteredParticipants.forEach((p) => {
      const key = p.teamName;
      if (!map[key]) map[key] = { coaches: 0, players: 0, eligible: 0 };
      if (p.role === UsersRoles.COACH) map[key].coaches++;
      if (p.role === UsersRoles.PLAYER) {
        map[key].players++;
        if (p.verified && !p.tooOld && waiverMap.get(p.id)) map[key].eligible++;
      }
    });
    return map;
  }, [filteredParticipants, waiverMap]);

  const teamBlocks = useMemo(() => {
    const blocks = [];
    let currentTeam = null;

    displayedParticipants.forEach((p) => {
      const teamKey = p.teamName ?? null;

      if (blocks.length === 0 || currentTeam !== teamKey) {
        currentTeam = teamKey;
        blocks.push({
          teamName: p.teamName,
          teamId: p.teamId,
          items: [],
        });
      }

      blocks[blocks.length - 1].items.push({ p });
    });

    return blocks;
  }, [displayedParticipants]);

  return (
    <div className='check-in-list'>
      <div className='sticky-top'>
        <div className='controls'>
          <FilterInput
            className='search'
            placeholder='Search'
            value={searchQuery}
            onChange={setSearchQuery}
            small
          />
          <Select
            options={divisions}
            value={division}
            onChange={handleDivisionChange}
            small
            className='division'
          />
          <Select
            options={teamsOptions}
            value={team}
            onChange={handleTeamChange}
            small
            className='team'
            search
          />
        </div>
      </div>

      <div className='blocks'>
        {/* The roster is the admitted teams matched against the organization's
            players and coaches, and each of the three arrives on its own
            listener — an empty check-in list reads as "nobody is admitted"
            rather than as "not read yet" */}
        {!isReady && <Spinner show inline />}
        {searchQuery.trim() && displayedParticipants.length === 0 && (
          <h5 className='name'>No results found for your search</h5>
        )}
        {teamBlocks.map((block) => {
          const { coaches, players, eligible } = teamTotals[block.teamName];

          return (
            <div
              className='block left-accent'
              key={`${block.teamName}-${block.teamId}`}
            >
              <div className='name-totals'>
                <Link to={'/teams/' + block.teamId} className='name'>
                  <h5>{block.teamName}</h5>
                </Link>
                <ul className='totals'>
                  <li className='badge'>
                    <span className='fa-flag primary' />
                    {`${coaches} Coach${coaches !== 1 ? 'es' : ''}`}
                  </li>
                  <li className='badge'>
                    <span className='fa-user primary' />
                    {`${players} Player${players !== 1 ? 's' : ''}`}
                  </li>
                  <li className='badge'>
                    <span className='fa-check-circle success' />
                    {`${eligible} Eligible`}
                  </li>
                  <li className='badge'>
                    <span className='fa-cross-circle danger' />
                    {`${players - eligible} Ineligible`}
                  </li>
                </ul>
              </div>

              <ul className='header tab-header'>
                <li className='col-1' />
                <li className='col-2'>Name, DOB</li>
                <li className='col-4'>Role</li>
                <li className='col-233a'>Name, DOB, Team, Division</li>
                <li className='col-233a4'>Name, DOB, Role, Team, Division</li>
                <li className='col-2a'>Status</li>
                <li className='col-3'>Division</li>
                <li className='col-3a'>Team</li>
                <li className='col-33a'>Team, Division</li>
                <li className='col-5'>Check-in</li>
              </ul>

              {block.items.map(({ p }) => {
                const isCoach = p.role === UsersRoles.COACH;
                const linkTo = isCoach
                  ? '/coaches/' + p.id
                  : '/players/' + p.id;

                const dob = p.birthDate?.format('MMM DD, YY');
                const checkInId = `${p.teamId}-${p.id}`;

                return (
                  <Link
                    key={`${p.id}-${p.teamId || 'no-team'}`}
                    to={linkTo}
                    className={'participant ' + p.role}
                  >
                    <Avatar user={p} size={64} className='col-1' />
                    <div className='col-2'>
                      <p className='font-medium upper'>{p.name}</p>
                      {dob && <p>DOB: {dob}</p>}
                    </div>
                    <div className='col-4'>
                      <p>
                        {isCoach && <span className='fa-flag gray-800'> </span>}
                        {p.roleLabel}
                      </p>
                    </div>
                    <div className='col-233a'>
                      <p>
                        <span className='font-medium upper'>{p.name}</span>
                        {dob && `, DOB: ${dob}`}
                      </p>
                      <p>{p.teamName}</p>
                      <p>{p.division}</p>
                    </div>
                    <div className='col-233a4'>
                      <div className='img-name-dob-wrap'>
                        <Avatar user={p} size={32} />
                        <div className='name-dob-wrap'>
                          <p className='font-medium upper'>
                            {p.name} -{' '}
                            {isCoach && (
                              <span className='fa-flag gray-800'> </span>
                            )}
                            {p.roleLabel.toUpperCase()}
                          </p>
                          {dob && <p>DOB: {dob}</p>}
                        </div>
                      </div>
                      <p>{p.teamName}</p>
                      <p>{p.division}</p>
                    </div>
                    <div className='col-2a'>
                      {p.role === UsersRoles.PLAYER && (
                        <PlayerStatus
                          verified={p.verified ?? false}
                          tooOld={p.tooOld}
                          waiverSigned={waiverMap.get(p.id)}
                        />
                      )}
                    </div>
                    <div className='col-3'>
                      <p>{p.division}</p>
                    </div>
                    <div className='col-3a'>
                      <p>{p.teamName}</p>
                    </div>
                    <div className='col-33a'>
                      <p>{p.teamName}</p>
                      <p>{p.division}</p>
                    </div>
                    <div className='col-5' onClick={(e) => e.preventDefault()}>
                      <CheckBox
                        small
                        label=''
                        ariaLabel={`Check in ${p.name}`}
                        value={!!checkedInMap[checkInId]?.status}
                        onChange={(val) =>
                          handleCheckChange(p.id, p.teamId, val)
                        }
                        className='check-in-checkbox'
                      />
                      <p>
                        {checkedInMap[checkInId]?.time &&
                        checkedInMap[checkInId]?.status
                          ? checkedInMap[checkInId]?.time.format(
                              'MMM DD, YY h:mma',
                            )
                          : ''}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
